import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { generateRandomId } from '../../utils/string';
import { Button, Form, Modal } from '@themesberg/react-bootstrap';
import DropdownComponent from '../../components/common/Dropdown';
import { arrayToDropdownOptions } from '../../utils/sms/arrayToDropdownOptions';
import { useDispatch, useSelector } from 'react-redux';
import { ErrorMessage } from '../scheme-management/Components/StyledComponents';
import { returnVendorDropdownOptions } from '../../utils/vehicles';
import { getVendorsListInRegion } from '../../parse-functions/vendors';
import { convertArrayToObj } from '../../utils/json';
import { updateSpinnerState } from '../../actions/spinner';
import { setVendorsListInStore } from '../../actions/vendors';
import { updateToastInfo } from '../../actions/settings';
import EditableDropDown from '../../components/common/EditableDropdown';
import DatePicker from '../tripPayments/DatePicker';
import TableOnly from '../scheme-management/Components/IndividualScheme/TableOnly';
import { infoTable } from '../../utils/sms/infoTable';
import { transformLogs } from './vehicleUtils';

const VehicleDetailsUpdateModal = (props) => {
  const { t } = useTranslation();
  const { showModal, closeModal, onConfirm, vehicle, setEditData } = props;
  const [formErrors, setFormErrors] = useState({});
  const isAdmin = useSelector(
    (state) => state?.user?.preferences?.isAdmin || false
  );
  const otherWarehouses = useSelector(
    (state) => state?.user?.otherWarehousesInZone || {}
  );
  const configData = useSelector((state) => state.user.config || {});
  const zone = useSelector((state) => state.user.warehouse.zone || '');
  const vendorsList = useSelector((state) => state?.vendors?.vendorsList || {});
  const dispatch = useDispatch();

  const getAvailableVendorList = async () => {
    dispatch(updateSpinnerState(true));
    try {
      const filters = {
        zone: isAdmin ? '' : zone,
        status: 'active',
        getAllVendors: isAdmin && true,
      };
      const vendors = await getVendorsListInRegion(filters);
      dispatch(
        setVendorsListInStore(
          convertArrayToObj(JSON.parse(JSON.stringify(vendors)), 'objectId')
        )
      );
    } catch (e) {
      dispatch(
        updateToastInfo({
          show: true,
          type: 'danger',
          title: t('Error'),
          message: t(e.message),
        })
      );
    } finally {
      dispatch(updateSpinnerState(false));
    }
  };

  useEffect(() => {
    if (Object.keys(vendorsList).length === 0) {
      getAvailableVendorList();
    }
  }, []);

  const handleInputChange = (field, value) => {
    setEditData((prev) => ({
      ...prev,
      [field]: value,
      ...(field === 'ongoing' && value === true ? { toDate: null } : {}),
    }));
  };
  const onConfirmClick = () => {
    const errors = {};

    if (!vehicle.driverName?.trim()) {
      errors.driverName = 'Please enter driver name';
    }

    if (!vehicle.contactNumber || !/^\d{10}$/.test(vehicle.contactNumber)) {
      errors.contactNumber = 'Enter valid 10-digit contact number';
    }

    if (!vehicle.storeName) {
      errors.storeName = 'Please choose a StoreName';
    }

    if (!vehicle.zone) {
      errors.zone = 'Please choose a Zone';
    }

    if (!vehicle.vendorName || !vehicle.vendorObjectId) {
      errors.vendorName = 'Please choose a Vendor';
    }

    if (!vehicle.fromDate) {
      errors.fromDate = 'Please choose a Date';
    }

    if (!vehicle.ongoing && !vehicle.toDate) {
      errors.toDate = 'Please choose a Date';
    }
    if (
      vehicle.fromDate &&
      vehicle.toDate &&
      !vehicle.ongoing &&
      new Date(vehicle.fromDate) > new Date(vehicle.toDate)
    ) {
      errors.toDate = 'To Date cannot be before From Date';
    }

    setFormErrors(errors);

    if (Object.keys(errors).length === 0) {
      onConfirm(vehicle);
    }
  };

  return (
    <Modal
      centered
      id={generateRandomId()}
      size="lg"
      show={showModal}
      onHide={closeModal}
    >
      <Modal.Header>
        <Modal.Title className="h6">
          {t(`Update Vehicle Details for ${vehicle?.vehicleNumber}`)}
        </Modal.Title>
        <Button variant="close" aria-label="Close" onClick={closeModal} />
      </Modal.Header>
      <Modal.Body>
        <div className="mb-1">Last Vehicle Update:</div>
        {transformLogs(vehicle?.actionLog, 'EditVehicle').length === 0 ? (
          <div className="text-center mb-3">
            <span className="text-muted">
              {t('No previous updates found for this vehicle.')}
            </span>
          </div>
        ) : (
          <TableOnly
            id="changeSummaryTable"
            data={transformLogs(vehicle?.actionLog, 'EditVehicle')}
            columns={infoTable()}
            isFooterRequired={false}
          />
        )}
        <div className="d-flex mb-3 justify-content-between align-items-center gap-2 mt-4 ">
          <Form.Group className="w-50">
            <Form.Label>{t('Vehicle Number')}</Form.Label>
            <Form.Control
              value={vehicle.vehicleNumber}
              disabled={true}
              type="text"
            />
          </Form.Group>
          <Form.Group className="w-50">
            <Form.Label>{t('Vehicle Type')}</Form.Label>
            <EditableDropDown
              width="100%"
              value={vehicle.vehicleType}
              onChange={(option) =>
                handleInputChange('vehicleType', option.value)
              }
              disabled={!isAdmin}
              options={arrayToDropdownOptions(configData.vehicleTypes)}
              placeholder={t('Choose Vehicle Type')}
            />
            {formErrors['vehicleType']?.length && (
              <ErrorMessage>{t('Please choose Vehicle Type')}</ErrorMessage>
            )}
          </Form.Group>
        </div>
        <div className="d-flex justify-content-between align-items-center gap-2 mb-3">
          <Form.Group className="w-50">
            <Form.Label>{t('Driver Name')}</Form.Label>
            <Form.Control
              value={vehicle.driverName}
              isInvalid={formErrors['driverName']?.length}
              type="text"
              onChange={(event) => {
                handleInputChange('driverName', event.target.value);
              }}
            />
            <Form.Control.Feedback type="invalid">
              {t('Please enter driver Name.')}
            </Form.Control.Feedback>
          </Form.Group>
          <Form.Group className="w-50">
            <Form.Label>{t('Driver Contact')}</Form.Label>
            <Form.Control
              value={vehicle.contactNumber}
              isInvalid={formErrors['contactNumber']?.length}
              type="number"
              onChange={(event) => {
                handleInputChange('contactNumber', event.target.value);
              }}
            />
            <Form.Control.Feedback type="invalid">
              {t(formErrors['contactNumber'])}
            </Form.Control.Feedback>
          </Form.Group>
        </div>
        <div className="d-flex justify-content-between align-items-center gap-2 mb-3">
          <Form.Group className="w-50">
            <Form.Label>{t('Store Name')}</Form.Label>
            <EditableDropDown
              width="100%"
              value={vehicle.storeName}
              onChange={(option) =>
                handleInputChange('storeName', option.value)
              }
              options={arrayToDropdownOptions(
                Object.values(otherWarehouses).map((item) => item.warehouseName)
              )}
              placeholder={t('Choose Store Name')}
            />
            {formErrors['storeName']?.length && (
              <ErrorMessage>{t('Please choose a StoreName')}</ErrorMessage>
            )}
          </Form.Group>
          <Form.Group className="w-50">
            <Form.Label>{t('Zone')}</Form.Label>
            <EditableDropDown
              width="100%"
              value={vehicle.zone}
              onChange={(option) => handleInputChange('zone', option.value)}
              options={arrayToDropdownOptions(
                isAdmin ? Object.keys(configData.zones) : [zone]
              )}
              placeholder={t('Choose Zone')}
            />
            {formErrors['zone']?.length && (
              <ErrorMessage>{t('Please choose a Zone')}</ErrorMessage>
            )}
          </Form.Group>
        </div>
        <Form.Group className="w-100 mb-1">
          <Form.Label>{t('Vendor Name')}</Form.Label>
          <EditableDropDown
            value={vehicle.vendorName}
            onChange={(option) => {
              setEditData((prev) => ({
                ...prev,
                vendorName: option.value?.vendorName,
                vendorObjectId: option.value?.objectId,
              }));
            }}
            options={returnVendorDropdownOptions(Object.values(vendorsList))}
            placeholder={t('Choose Vendor Name')}
          />
        </Form.Group>
        <span className="fs-6">
          Note: Can't find the vendor? Ensure it's active, or&nbsp;
          <a
            href="/vendor/add"
            target="_blank"
            rel="noopener noreferrer"
            style={{ fontWeight: 'bold', textDecoration: 'underline' }}
          >
            +Add New
          </a>
          &nbsp; to create one.
        </span>
        <div className="mt-3">
          <Form.Label>Select the Date</Form.Label>
        </div>
        <div className="d-flex justify-content-between align-items-start gap-2">
          {/* From Date */}
          <Form.Group className="w-100">
            <Form.Label>{t(`Update From`)}</Form.Label>
            <DatePicker
              value={vehicle.fromDate}
              setValue={(date) => {
                handleInputChange('fromDate', date);
              }}
              isInvalid={formErrors.fromDate}
            />
            {formErrors['fromDate'] && (
              <ErrorMessage>{t('Please choose From Date')}</ErrorMessage>
            )}
          </Form.Group>

          {/* To Date + Checkbox */}
          <Form.Group className="w-100 mb-3">
            <Form.Label>{t(`Update Till`)}</Form.Label>
            <DatePicker
              value={vehicle.toDate}
              setValue={(date) => {
                handleInputChange('toDate', date);
              }}
              isInvalid={formErrors.toDate}
              disabled={vehicle.ongoing}
            />
            {formErrors.toDate && (
              <ErrorMessage type="invalid">{t(formErrors.toDate)}</ErrorMessage>
            )}

            {/* Checkbox directly below To Date */}
            <Form.Check
              className="mt-2"
              type="checkbox"
              label={t(`Ongoing until Changed`)}
              checked={vehicle.ongoing}
              onChange={(event) => {
                handleInputChange('ongoing', event.target.checked);
              }}
            />
          </Form.Group>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className="d-flex">
          <Button
            style={{ backgroundColor: '#F0F0F0' }}
            className="mx-2 mt-3"
            variant="outline"
            onClick={() => closeModal()}
          >
            {t('Cancel')}
          </Button>
          <Button
            style={{ width: '98%' }}
            className="mx-2 mt-4"
            variant="primary"
            onClick={() => onConfirmClick()}
          >
            {t(`Confirm`)}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};
export default VehicleDetailsUpdateModal;
